import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import SyncIcon from "@mui/icons-material/Sync";
import { Link as RouterLink, Navigate } from "react-router-dom";
import { toast } from "react-toastify";

import DistributionReviewApi from "../../../../../infrastructure/api/distributionReview";
import { ModalTransition } from "../../../../components/shared/Dialog/Config";
import EnhancedTable from "../../../../components/shared/table";
import { canManageDistributionTeam } from "../../../../utils/authRoleHelpers";
import DistributionCompanySelect from "../../DistributionCompanySelect";
import EDCBreadcrumbs, { EDC_MANAGEMENT_CRUMB, EDC_ROOT_CRUMB } from "../../EDCBreadcrumbs";
import { EDC_PATHS } from "../../edcPaths";
import useDistributionPortalContext, { pick } from "../../useDistributionPortalContext";

const USER_TYPES = {
  companyAdmin: "companyAdmin",
  areaStaff: "areaStaff",
};

const emptyAreaAccess = { distributionAreaId: "", canAreaReview: true, canEngineeringReview: false };

const emptyUser = {
  userId: null,
  userType: USER_TYPES.areaStaff,
  firstName: "",
  lastName: "",
  mobile: "",
  position: "",
  phone: "",
  email: "",
  password: "",
  isActive: true,
  areas: [{ ...emptyAreaAccess }],
};

const accessText = (a) =>
  [
    pick(a, "canAreaReview", "CanAreaReview") ? "ناظر امور" : null,
    pick(a, "canEngineeringReview", "CanEngineeringReview") ? "واحد مهندسی" : null,
  ]
    .filter(Boolean)
    .join(" + ");

const toAreaRows = (u) =>
  (pick(u, "areas", "Areas") || []).map((a) => ({
    distributionAreaId: String(pick(a, "distributionAreaId", "DistributionAreaId")),
    canAreaReview: Boolean(pick(a, "canAreaReview", "CanAreaReview")),
    canEngineeringReview: Boolean(pick(a, "canEngineeringReview", "CanEngineeringReview")),
  }));

const buildSavePayload = (companyId, form) => {
  const asAdmin = form.userType === USER_TYPES.companyAdmin;
  return {
    userId: form.userId || null,
    distributionCompanyId: Number(companyId),
    firstName: form.firstName?.trim() || null,
    lastName: form.lastName?.trim() || null,
    mobile: String(form.mobile || "").trim(),
    position: form.position?.trim() ?? "",
    phone: form.phone?.trim() ?? "",
    email: form.email?.trim() ?? "",
    password: form.password?.trim() || null,
    isCompanyAdmin: asAdmin,
    isActive: Boolean(form.isActive),
    areas: asAdmin
      ? []
      : form.areas
          .filter((a) => Number(a.distributionAreaId) > 0)
          .map((a) => ({
            distributionAreaId: Number(a.distributionAreaId),
            canAreaReview: Boolean(a.canAreaReview),
            canEngineeringReview: Boolean(a.canEngineeringReview),
          })),
  };
};

const validateForm = (form) => {
  if (!String(form.firstName || "").trim() || !String(form.lastName || "").trim())
    return "نام و نام خانوادگی الزامی است (در پیامک‌ها با نام کامل خطاب می‌شود)";
  if (!String(form.mobile || "").trim()) return "شماره موبایل الزامی است (برای ورود و پیامک ارجاع)";
  if (form.userType === USER_TYPES.companyAdmin) return null;
  const rows = form.areas.filter((a) => Number(a.distributionAreaId) > 0);
  if (rows.length === 0) return "حداقل یک امور انتخاب کنید";
  const ids = rows.map((a) => a.distributionAreaId);
  if (new Set(ids).size !== ids.length) return "یک امور دو بار انتخاب شده است";
  if (rows.some((a) => !a.canAreaReview && !a.canEngineeringReview))
    return "برای هر امور حداقل یکی از دسترسی‌های ناظر امور یا واحد مهندسی را فعال کنید";
  return null;
};

const EDCUsersPage = () => {
  const { context, companyId, companyName, isCompanyAdmin, setSelectedCompanyId, reload, loading, error } =
    useDistributionPortalContext();
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [areaFilter, setAreaFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dialog, setDialog] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toggleTarget, setToggleTarget] = useState(null);

  const areas = useMemo(() => pick(context, "areas", "Areas") || [], [context]);
  const areaById = useMemo(() => {
    const map = new Map();
    areas.forEach((a) => map.set(Number(pick(a, "id", "Id")), a));
    return map;
  }, [areas]);

  const loadUsers = useCallback(async () => {
    if (!companyId || !isCompanyAdmin) {
      setUsers([]);
      return;
    }
    setUsersLoading(true);
    try {
      const res = await DistributionReviewApi.listUsers(companyId);
      const list = res?.data ?? res?.Data ?? [];
      setUsers(Array.isArray(list) ? list : []);
    } catch (e) {
      toast.error(e?.message || "خطا در دریافت کاربران");
    } finally {
      setUsersLoading(false);
    }
  }, [companyId, isCompanyAdmin]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const rows = useMemo(
    () =>
      users
        .filter((u) => {
          const admin = pick(u, "isCompanyAdmin", "IsCompanyAdmin") === true;
          const active = pick(u, "isActive", "IsActive") !== false;
          if (typeFilter === "admin" && !admin) return false;
          if (typeFilter === "staff" && admin) return false;
          if (statusFilter === "active" && !active) return false;
          if (statusFilter === "inactive" && active) return false;
          if (
            areaFilter &&
            !(pick(u, "areas", "Areas") || []).some(
              (a) => String(pick(a, "distributionAreaId", "DistributionAreaId")) === areaFilter
            )
          )
            return false;
          return true;
        })
        .map((u) => {
          const admin = pick(u, "isCompanyAdmin", "IsCompanyAdmin") === true;
          const userAreas = pick(u, "areas", "Areas") || [];
          return {
            raw: u,
            userId: pick(u, "userId", "UserId"),
            displayName: pick(u, "displayName", "DisplayName") || "—",
            mobile: pick(u, "mobile", "Mobile") || "",
            position: pick(u, "position", "Position") || "",
            phone: pick(u, "phone", "Phone") || "",
            userTypeTitle: admin ? "مدیر شرکت" : "کارشناس امور",
            isCompanyAdmin: admin,
            areasTitle: admin
              ? "همه امورها"
              : userAreas
                  .map(
                    (a) =>
                      `${pick(a, "distributionAreaName", "DistributionAreaName") || "—"} (${accessText(a)})`
                  )
                  .join("، "),
            areaItems: userAreas,
            isActive: pick(u, "isActive", "IsActive") !== false,
            joinedAt: pick(u, "joinedAt", "JoinedAt"),
          };
        }),
    [users, areaFilter, typeFilter, statusFilter]
  );

  const summary = useMemo(() => {
    const active = users.filter((u) => pick(u, "isActive", "IsActive") !== false).length;
    const admins = users.filter((u) => pick(u, "isCompanyAdmin", "IsCompanyAdmin") === true).length;
    return { total: users.length, active, inactive: users.length - active, admins };
  }, [users]);

  const headCells = [
    { id: "displayName", label: "نام و نام خانوادگی" },
    { id: "mobile", label: "موبایل (نام کاربری)" },
    { id: "position", label: "سمت", render: (v) => v || "—" },
    {
      id: "userTypeTitle",
      label: "نوع کاربر",
      render: (v, row) => (
        <Chip size="small" color={row.isCompanyAdmin ? "primary" : "default"} label={v} />
      ),
    },
    {
      id: "areasTitle",
      label: "امور و دسترسی",
      render: (v, row) =>
        row.isCompanyAdmin ? (
          <Typography variant="body2" color="text.secondary">
            دسترسی کامل به همه امورها
          </Typography>
        ) : (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {row.areaItems.map((a) => (
              <Chip
                key={pick(a, "membershipId", "MembershipId")}
                size="small"
                variant="outlined"
                label={`${pick(a, "distributionAreaName", "DistributionAreaName") || "—"}: ${accessText(a)}`}
              />
            ))}
          </Stack>
        ),
    },
    {
      id: "isActive",
      label: "وضعیت",
      type: "boolean",
      model: { active: "فعال", deActive: "غیرفعال" },
    },
    { id: "joinedAt", label: "تاریخ عضویت", type: "date" },
  ];

  const openNew = () => setDialog({ ...emptyUser, areas: [{ ...emptyAreaAccess }] });

  const openEdit = (u) => {
    const userAreas = toAreaRows(u);
    const admin = pick(u, "isCompanyAdmin", "IsCompanyAdmin") === true;
    setDialog({
      userId: pick(u, "userId", "UserId"),
      userType: admin ? USER_TYPES.companyAdmin : USER_TYPES.areaStaff,
      firstName: pick(u, "firstName", "FirstName") || "",
      lastName: pick(u, "lastName", "LastName") || "",
      mobile: pick(u, "mobile", "Mobile") || "",
      position: pick(u, "position", "Position") || "",
      phone: pick(u, "phone", "Phone") || "",
      email: pick(u, "email", "Email") || "",
      password: "",
      isActive: pick(u, "isActive", "IsActive") !== false,
      areas: userAreas.length ? userAreas : [{ ...emptyAreaAccess }],
    });
  };

  const setField = (field, value) => setDialog((s) => ({ ...s, [field]: value }));

  const updateAreaRow = (index, patch) =>
    setDialog((s) => ({
      ...s,
      areas: s.areas.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    }));

  const removeAreaRow = (index) =>
    setDialog((s) => ({ ...s, areas: s.areas.filter((_, i) => i !== index) }));

  const addAreaRow = () =>
    setDialog((s) => ({ ...s, areas: [...s.areas, { ...emptyAreaAccess }] }));

  const save = async () => {
    if (!companyId || !dialog) return;
    const validation = validateForm(dialog);
    if (validation) {
      toast.error(validation);
      return;
    }
    setSaving(true);
    try {
      await DistributionReviewApi.saveUser(buildSavePayload(companyId, dialog));
      toast.success(dialog.userId ? "تغییرات کاربر ذخیره شد" : "کاربر ایجاد شد و اطلاعات ورود پیامک شد");
      setDialog(null);
      await Promise.all([loadUsers(), reload()]);
    } catch (e) {
      toast.error(e?.message || "خطا در ذخیره کاربر");
    } finally {
      setSaving(false);
    }
  };

  const confirmToggle = async () => {
    if (!toggleTarget || !companyId) return;
    const u = toggleTarget.raw;
    const admin = pick(u, "isCompanyAdmin", "IsCompanyAdmin") === true;
    setSaving(true);
    try {
      await DistributionReviewApi.saveUser(
        buildSavePayload(companyId, {
          userId: pick(u, "userId", "UserId"),
          userType: admin ? USER_TYPES.companyAdmin : USER_TYPES.areaStaff,
          firstName: pick(u, "firstName", "FirstName") || "",
          lastName: pick(u, "lastName", "LastName") || "",
          mobile: pick(u, "mobile", "Mobile") || "",
          position: pick(u, "position", "Position") ?? "",
          phone: pick(u, "phone", "Phone") ?? "",
          email: pick(u, "email", "Email") ?? "",
          password: "",
          isActive: !toggleTarget.isActive,
          areas: toAreaRows(u),
        })
      );
      toast.success(toggleTarget.isActive ? "کاربر غیرفعال شد" : "کاربر فعال شد");
      setToggleTarget(null);
      await Promise.all([loadUsers(), reload()]);
    } catch (e) {
      toast.error(e?.message || "خطا در تغییر وضعیت کاربر");
    } finally {
      setSaving(false);
    }
  };

  if (!canManageDistributionTeam()) return <Navigate to={EDC_PATHS.root} replace />;
  if (context && !isCompanyAdmin) return <Navigate to={EDC_PATHS.root} replace />;

  const dialogIsAdmin = dialog?.userType === USER_TYPES.companyAdmin;
  const busy = loading || usersLoading;

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <EDCBreadcrumbs
          crumbs={[EDC_ROOT_CRUMB, EDC_MANAGEMENT_CRUMB, { title: "کاربران", to: EDC_PATHS.users }]}
        />
      </Grid>

      {error ? (
        <Grid item xs={12}>
          <Alert severity="error">{error}</Alert>
        </Grid>
      ) : null}

      <Grid item xs={12}>
        <Card>
          <CardHeader
            title="کاربران شرکت توزیع"
            subheader={
              companyName
                ? `${companyName} — ${summary.total} کاربر (${summary.active} فعال${summary.inactive ? `، ${summary.inactive} غیرفعال` : ""}، ${summary.admins} مدیر)`
                : "هر کاربر می‌تواند در چند امور ناظر یا کارشناس واحد مهندسی باشد"
            }
            action={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <DistributionCompanySelect context={context} onChange={setSelectedCompanyId} />
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={openNew}
                  disabled={!companyId || areas.length === 0}
                >
                  ایجاد
                </Button>
                <Divider orientation="vertical" variant="middle" flexItem />
                <Tooltip title="به‌روزرسانی">
                  <span>
                    <IconButton color="primary" onClick={loadUsers} disabled={busy}>
                      <SyncIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            }
          />
          <CardContent>
            {companyId && !loading && areas.length === 0 ? (
              <Alert
                severity="info"
                sx={{ mb: 2 }}
                action={
                  <Button component={RouterLink} to={EDC_PATHS.areas} size="small">
                    تعریف امور
                  </Button>
                }
              >
                ابتدا امورهای شرکت را تعریف کنید تا بتوانید کاربران را به امور بسپارید.
              </Alert>
            ) : null}

            <Box display="flex" gap={2} mb={2} flexWrap="wrap">
              <TextField
                select
                size="small"
                label="امور"
                value={areaFilter}
                onChange={(e) => setAreaFilter(e.target.value)}
                sx={{ minWidth: 200 }}
              >
                <MenuItem value="">همه امور</MenuItem>
                {areas.map((a) => (
                  <MenuItem key={pick(a, "id", "Id")} value={String(pick(a, "id", "Id"))}>
                    {pick(a, "name", "Name")}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="نوع کاربر"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                sx={{ minWidth: 160 }}
              >
                <MenuItem value="">همه</MenuItem>
                <MenuItem value="admin">مدیر شرکت</MenuItem>
                <MenuItem value="staff">کارشناس امور</MenuItem>
              </TextField>
              <TextField
                select
                size="small"
                label="وضعیت"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                sx={{ minWidth: 140 }}
              >
                <MenuItem value="">همه</MenuItem>
                <MenuItem value="active">فعال</MenuItem>
                <MenuItem value="inactive">غیرفعال</MenuItem>
              </TextField>
            </Box>

            <EnhancedTable
              rowKey="userId"
              rows={rows}
              headCells={headCells}
              pageSize={100}
              loading={busy}
              defaultSort={{ orderBy: "displayName", order: "asc" }}
              actions={[
                {
                  icon: <EditIcon sx={{ width: "1rem", height: "1rem" }} />,
                  tooltip: "ویرایش",
                  onClick: (row) => openEdit(row.raw),
                },
                {
                  icon: <BlockIcon sx={{ width: "1rem", height: "1rem" }} />,
                  tooltip: "فعال / غیرفعال کردن",
                  color: "warning",
                  onClick: (row) => setToggleTarget(row),
                },
              ]}
            />
          </CardContent>
        </Card>
      </Grid>

      <Dialog
        open={Boolean(dialog)}
        onClose={() => !saving && setDialog(null)}
        TransitionComponent={ModalTransition}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{dialog?.userId ? "ویرایش کاربر" : "ایجاد کاربر"}</DialogTitle>
        <Divider />
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                نوع کاربر
              </Typography>
              <RadioGroup
                row
                value={dialog?.userType || USER_TYPES.areaStaff}
                onChange={(e) => setField("userType", e.target.value)}
              >
                <FormControlLabel value={USER_TYPES.areaStaff} control={<Radio />} label="کارشناس امور / واحد مهندسی" />
                <FormControlLabel value={USER_TYPES.companyAdmin} control={<Radio />} label="مدیر شرکت" />
              </RadioGroup>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" fontWeight={700}>
                مشخصات
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="نام"
                value={dialog?.firstName || ""}
                onChange={(e) => setField("firstName", e.target.value)}
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="نام خانوادگی"
                value={dialog?.lastName || ""}
                onChange={(e) => setField("lastName", e.target.value)}
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="سمت"
                value={dialog?.position || ""}
                onChange={(e) => setField("position", e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="شماره موبایل"
                value={dialog?.mobile || ""}
                onChange={(e) => setField("mobile", e.target.value)}
                required
                fullWidth
                inputProps={{ dir: "ltr" }}
                helperText="نام کاربری ورود و مقصد پیامک ارجاع"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="تلفن"
                value={dialog?.phone || ""}
                onChange={(e) => setField("phone", e.target.value)}
                fullWidth
                inputProps={{ dir: "ltr" }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="ایمیل"
                value={dialog?.email || ""}
                onChange={(e) => setField("email", e.target.value)}
                fullWidth
                inputProps={{ dir: "ltr" }}
              />
            </Grid>
            {!dialog?.userId ? (
              <Grid item xs={12} md={4}>
                <TextField
                  label="رمز عبور اولیه (اختیاری)"
                  type="password"
                  autoComplete="new-password"
                  value={dialog?.password || ""}
                  onChange={(e) => setField("password", e.target.value)}
                  fullWidth
                  helperText="خالی = ۱۲۳۴۵۶؛ اطلاعات ورود پیامک می‌شود"
                />
              </Grid>
            ) : null}
            <Grid item xs={12} md={dialog?.userId ? 12 : 8} sx={{ display: "flex", alignItems: "center" }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={dialog?.isActive !== false}
                    onChange={(e) => setField("isActive", e.target.checked)}
                  />
                }
                label={dialog?.isActive !== false ? "حساب فعال است" : "حساب غیرفعال است (امکان ورود ندارد)"}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>

            {dialogIsAdmin ? (
              <Grid item xs={12}>
                <Alert severity="info">مدیر شرکت به همه امورها، کاربران و کارتابل‌ها دسترسی کامل دارد.</Alert>
              </Grid>
            ) : (
              <Grid item xs={12}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    امور و دسترسی‌ها
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={addAreaRow}
                    disabled={areas.length === 0 || (dialog?.areas || []).length >= areas.length}
                  >
                    افزودن امور
                  </Button>
                </Stack>
                <Stack spacing={1}>
                  {(dialog?.areas || []).map((row, index) => {
                    const area = areaById.get(Number(row.distributionAreaId));
                    const engineeringOff =
                      area && !pick(area, "engineeringUnitEnabled", "EngineeringUnitEnabled");
                    return (
                      <Stack
                        key={index}
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                        alignItems={{ xs: "stretch", sm: "center" }}
                        sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1 }}
                      >
                        <TextField
                          select
                          size="small"
                          label="امور"
                          value={row.distributionAreaId}
                          onChange={(e) => updateAreaRow(index, { distributionAreaId: e.target.value })}
                          sx={{ minWidth: 220 }}
                        >
                          {areas.map((a) => {
                            const id = String(pick(a, "id", "Id"));
                            const takenElsewhere = dialog.areas.some(
                              (r, i) => i !== index && r.distributionAreaId === id
                            );
                            return (
                              <MenuItem key={id} value={id} disabled={takenElsewhere}>
                                {pick(a, "name", "Name")}
                              </MenuItem>
                            );
                          })}
                        </TextField>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={Boolean(row.canAreaReview)}
                              onChange={(e) => updateAreaRow(index, { canAreaReview: e.target.checked })}
                            />
                          }
                          label="ناظر امور"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={Boolean(row.canEngineeringReview)}
                              onChange={(e) =>
                                updateAreaRow(index, { canEngineeringReview: e.target.checked })
                              }
                            />
                          }
                          label="واحد مهندسی"
                        />
                        {engineeringOff && row.canEngineeringReview ? (
                          <Typography variant="caption" color="warning.main">
                            واحد مهندسی این امور غیرفعال است
                          </Typography>
                        ) : null}
                        <Box sx={{ flexGrow: 1 }} />
                        <Tooltip title="حذف امور">
                          <span>
                            <IconButton
                              color="error"
                              onClick={() => removeAreaRow(index)}
                              disabled={dialog.areas.length <= 1}
                            >
                              <DeleteOutlineIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    );
                  })}
                </Stack>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={() => setDialog(null)} disabled={saving}>
            انصراف
          </Button>
          <Button
            variant="contained"
            disabled={saving}
            onClick={save}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            ذخیره
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(toggleTarget)}
        onClose={() => !saving && setToggleTarget(null)}
        TransitionComponent={ModalTransition}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{toggleTarget?.isActive ? "غیرفعال کردن کاربر" : "فعال کردن کاربر"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {toggleTarget?.isActive
              ? `با غیرفعال شدن «${toggleTarget?.displayName}»، امکان ورود به پورتال و دریافت پیامک ارجاع از او گرفته می‌شود. ادامه می‌دهید؟`
              : `«${toggleTarget?.displayName}» دوباره امکان ورود به پورتال و دریافت پیامک ارجاع خواهد داشت. ادامه می‌دهید؟`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToggleTarget(null)} disabled={saving}>
            انصراف
          </Button>
          <Button
            variant="contained"
            color={toggleTarget?.isActive ? "warning" : "success"}
            onClick={confirmToggle}
            disabled={saving}
            startIcon={
              saving ? (
                <CircularProgress size={16} color="inherit" />
              ) : toggleTarget?.isActive ? (
                <BlockIcon />
              ) : (
                <CheckCircleOutlineIcon />
              )
            }
          >
            {toggleTarget?.isActive ? "غیرفعال شود" : "فعال شود"}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default EDCUsersPage;
